import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import update from 'immutability-helper';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Button, Typography } from '@material-ui/core';
import TimePeriodSelector from '../acquisition/TimePeriodSelector';
import DateInput from '../acquisition/DateInput';
import StepperButtons from '../acquisition/StepperButtons';
import CloudSelector from '../acquisition/CloudSelector';
import ActivityIndicator from '../core/ActivityIndicator.jsx';
import ImageTable from '../visualization/ImageTable';
import { getImageKey } from '../visualization/imageCsv';
import ImportDatesButton from '../acquisition/ImportDatesButton';
import { Actions as Mepbay } from '../../../store/ducks/mepbay';
import { FINALIZE } from '../../../hooks/useStepNavigations';
import { formatDate, formatDateDiff, datesBetween, useLocalStorage } from '../../../common/utils';
import ReactGA from 'react-ga';
import TourGuider from '../tour/TourGuider';
import moment from 'moment';
import HelpButton from '../core/HelpButton';
import { uniteMissionsDates } from '../../../common/algorithms.js';
import { useErrorNotice } from '../../../hooks/useErrorNotice';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: theme.spacing(2),
  },
  description: {
    margin: theme.spacing(7, 0, 3),
  },
  margin: {
    margin: '5px',
  },
}));

const toTimestamp = (date) => parseInt(moment(date).format('x'), 10);
const toISOString = (timestamp) => moment(timestamp).toISOString();
const normalizePeriod = ([start, end]) => (moment(end).isBefore(moment(start)) ? [end, start] : [start, end]);

/**
 * @function PeriodImageChooser
 * @description Merges MEPBay's period/cloud search and the resulting image-selection table into
 * a single step, so the user can adjust the period or cloud filter and see the candidate list
 * update immediately, without navigating to a separate step to see it and back again to change
 * the dates. The user picks a period and cloud filter first, then clicks "search" — same flow as
 * Shoreline/ShoreR — instead of the whole mission history loading upfront.
 * @param {Object} props - Component properties.
 * @param {Function} props.navigate - Step navigation handler, forwarded to StepperButtons.
 * **Called by:
 * @see {MepBayPage} - as the third acquisition step.
 * @memberof module:Mepbay
 * @returns {JSX.Element} The period/cloud-coverage and image selection UI, or a loading indicator.
 */
const PeriodImageChooser = ({ navigate }) => {
  const dates = useSelector((state) => state.mepbay.availableDates, shallowEqual);
  const datesVersion = useSelector((state) => state.mepbay.availableDatesVersion);
  const missions = useSelector((state) => state.mepbay.missions, shallowEqual);
  const metadata = useSelector((state) => state.mepbay.metadata);
  const coordinates = useSelector((state) => state.mepbay.coordinates, shallowEqual);
  const savedPeriod = useSelector((state) => [state.mepbay.start, state.mepbay.end], shallowEqual);

  const dispatch = useDispatch();
  const [t] = useTranslation();
  const notifyError = useErrorNotice();
  const classes = useStyles();

  const [cloudCoverage, setcloudCoverage] = useState(() => {
    const saved = Number(window.sessionStorage.getItem('mepbayCloudCoverage'));
    return Number.isFinite(saved) && saved > 0 ? saved : 0.05;
  });
  const [period, setPeriodRaw] = useState(() =>
    savedPeriod[0] && savedPeriod[1]
      ? normalizePeriod(savedPeriod)
      : normalizePeriod([moment().subtract(1, 'year').toISOString(), moment().toISOString()]),
  );
  const setPeriod = (next) => setPeriodRaw(normalizePeriod(next));
  const [start, end] = period;

  const [searched, setSearched] = useState(Boolean(dates) && dates.length > 0);
  const hasResults = searched && Boolean(dates) && dates.length > 0;
  const [isSearching, setIsSearching] = useState(false);
  const [isTourOpen, setIsTourOpen] = useLocalStorage('showPeriodTour', true);

  const [selected, setSelected] = useState(() => {
    try {
      return JSON.parse(window.sessionStorage.getItem('mepbayImageSelection') || '{}');
    } catch {
      return {};
    }
  });
  const narrowedSignatureRef = useRef(null);

  const onChange = (newStart, newEnd) => {
    if (moment(newEnd).isBefore(moment(newStart))) {
      notifyError(t('forms.acquisition.3.warning'));
      setPeriod([newStart, newStart]);
      return;
    }
    setPeriod([newStart, newEnd]);
  };

  /**
   * @function (search-finished effect)
   * @description Releases the "searching" state as soon as a search comes back. Keyed on
   * `availableDatesVersion` rather than on `dates`: a search that finds no images leaves `dates`
   * shallow-equal to the previous empty result, so the selector hands back the same array
   * identity and an effect watching `dates` never re-runs, leaving the search button disabled
   * with no message. See `ducks/acquisition/reducer.js`.
   * @memberof module:Mepbay
   * @returns {void}
   */
  useEffect(() => {
    setIsSearching(false);
  }, [datesVersion]);

  useEffect(() => {
    window.sessionStorage.setItem('mepbayCloudCoverage', cloudCoverage);
  }, [cloudCoverage]);

  useEffect(() => {
    if (start && end) dispatch(Mepbay.setPeriod(start, end));
  }, [start, end, dispatch]);

  /**
   * @function handleSearch
   * @description Loads MEPBay's available images for the current AOI and period, skipping the
   * Earth Engine query when it's the exact same AOI/period as the last successful search and
   * results are already on hand. The cloud filter is applied client-side afterwards, over
   * whatever the period query returned.
   * @returns {void}
   */
  const handleSearch = () => {
    const signature = JSON.stringify({ start, end, coordinates });
    if (
      signature === window.sessionStorage.getItem('mepbayLastSearchSignature') &&
      Array.isArray(dates) &&
      dates.length > 0
    ) {
      setSearched(true);
      return;
    }
    window.sessionStorage.setItem('mepbayLastSearchSignature', signature);
    setIsSearching(true);
    setSearched(true);
    dispatch(Mepbay.loadAvailableImages(start, end));
  };

  const { visibleDates, filteredOutByCloud, narrowedDates } = useMemo(() => {
    if (!hasResults) return { visibleDates: [], filteredOutByCloud: false, narrowedDates: [] };
    const visible = uniteMissionsDates(missions).filter((entry) => entry.content <= cloudCoverage);
    if (visible.length === 0) return { visibleDates: visible, filteredOutByCloud: true, narrowedDates: [] };
    let begin = new Date(start);
    begin.setDate(begin.getDate() - 1);
    begin = toISOString(toTimestamp(begin));
    const narrowed = visible.filter((entry) => entry.date >= begin && entry.date <= end).reverse();
    return { visibleDates: visible, filteredOutByCloud: false, narrowedDates: narrowed };
  }, [hasResults, missions, cloudCoverage, start, end]);

  const visibleDateValues = visibleDates.map((entry) => entry.date).sort();
  const clampToVisible = (date) => {
    if (!visibleDateValues.length || !date) return date;
    if (new Date(date) < new Date(visibleDateValues[0])) return visibleDateValues[0];
    if (new Date(date) > new Date(visibleDateValues[visibleDateValues.length - 1]))
      return visibleDateValues[visibleDateValues.length - 1];
    return date;
  };
  const sliderStart = clampToVisible(start);
  const sliderEnd = clampToVisible(end);

  // Merges new keys in (defaulting to checked) and drops stale ones, keeping whatever the user
  // already (un)checked for images that remain in view — matches the original two-step flow.
  // Skips entirely while narrowedDates is empty (e.g. a transient render before period/cloud
  // settle) so a real, non-empty selection restored from sessionStorage is never wiped out.
  useEffect(() => {
    if (!narrowedDates.length) return;
    const signature = narrowedDates.map(getImageKey).join('|');
    if (signature === narrowedSignatureRef.current) return;
    narrowedSignatureRef.current = signature;
    setSelected((prev) => {
      const next = { ...prev };
      narrowedDates.forEach((img) => {
        const key = getImageKey(img);
        if (next[key] === undefined) next[key] = true;
      });
      const validKeys = new Set(narrowedDates.map(getImageKey));
      Object.keys(next).forEach((k) => {
        if (!validKeys.has(k)) delete next[k];
      });
      return next;
    });
  }, [narrowedDates]);

  useEffect(() => {
    window.sessionStorage.setItem('mepbayImageSelection', JSON.stringify(selected));
  }, [selected]);

  // Debounced so dragging the period slider doesn't fire a thumbnail request (and its
  // cancellation) on every intermediate tick — only once the narrowed list settles.
  useEffect(() => {
    if (!narrowedDates.length) return undefined;
    const handle = setTimeout(() => dispatch(Mepbay.loadThumbnails(narrowedDates)), 400);
    return () => {
      clearTimeout(handle);
      dispatch(Mepbay.cancelThumbnails());
    };
  }, [narrowedDates, dispatch]);

  const handleCheckboxChange = (key, checked) => {
    setSelected((prev) => update(prev, { [key]: { $set: checked } }));
  };

  const handleSelectAll = (keys, checked) => {
    setSelected((prev) => {
      const changes = keys.reduce((acc, key) => {
        acc[key] = { $set: checked };
        return acc;
      }, {});
      return update(prev, changes);
    });
  };

  /**
   * @function handleReloadFullList
   * @description Restores the full candidate list backed up by handleFinish (see below) — both the
   * flattened dates AND the raw per-mission date dictionaries. The missions half matters because
   * handleFinish's own setAvailableDates call (with no missions argument) makes the reducer
   * *narrow* state.mepbay.missions down to just the checked dates, not just state.mepbay
   * .availableDates — so restoring only the flat list would leave narrowedDates (which is derived
   * from missions, not availableDates) still computing from the narrowed set. Also widens the
   * period back out to cover the backup and resets the cloud filter to 100%, otherwise the
   * restored list would just get immediately re-narrowed by whatever filter is still set locally.
   * **Called by:
   * @see {PeriodImageChooser} - via the "reload full list" button's onClick.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleReloadFullList = () => {
    try {
      const backup = JSON.parse(window.sessionStorage.getItem('mepbayFullImageList') || 'null');
      if (backup?.dates?.length) {
        const sorted = [...backup.dates].sort((a, b) => new Date(a.date) - new Date(b.date));
        setcloudCoverage(1);
        setPeriod([sorted[0].date, sorted[sorted.length - 1].date]);
        dispatch(Mepbay.setAvailableDates(backup.dates, backup.missions));
        dispatch(Mepbay.loadThumbnails(backup.dates));
      }
    } catch {}
  };

  let fullListBackup = null;
  try {
    fullListBackup = JSON.parse(window.sessionStorage.getItem('mepbayFullImageList') || 'null');
  } catch {
    fullListBackup = null;
  }
  const canReloadFullList = Boolean(fullListBackup?.dates?.length > narrowedDates.length);

  /**
   * @function handleFinish
   * @description Logs the chosen cloud coverage/period to Google Analytics, backs up the full
   * (pre-selection) narrowed list AND its current missions (see handleReloadFullList for why the
   * missions half is needed) to sessionStorage, then commits only the checked images to the
   * MEPBay acquisition state. Only overwrites the backup when the current list is at least as
   * large, so repeatedly finalizing an already-narrowed list can't shrink it.
   * **Called by:
   * @see {PeriodImageChooser} - via StepperButtons' onNext.
   * @memberof module:Mepbay
   * @returns {void}
   */
  const handleFinish = () => {
    ReactGA.event({ category: 'Mepbay', action: 'SetcloudCoverage', value: cloudCoverage });
    ReactGA.event({ category: 'Mepbay', action: 'SetPeriod', value: start + '-' + end });

    try {
      const existing = JSON.parse(window.sessionStorage.getItem('mepbayFullImageList') || 'null');
      if (!existing || narrowedDates.length >= existing.dates.length) {
        window.sessionStorage.setItem('mepbayFullImageList', JSON.stringify({ dates: narrowedDates, missions }));
      }
    } catch {
      window.sessionStorage.setItem('mepbayFullImageList', JSON.stringify({ dates: narrowedDates, missions }));
    }

    const filtered = narrowedDates.filter((img) => selected[getImageKey(img)]);
    dispatch(Mepbay.setAvailableDates(filtered));
  };

  const steps = [
    { selector: '#timeselector', content: t('tour.acquisition.3.period') },
    { selector: '#timeselector', content: t('tour.acquisition.3.point') },
    { selector: '#cloudselector', content: t('tour.acquisition.3.cloud') },
  ];
  if (hasResults && !filteredOutByCloud) {
    steps.push(
      { selector: '#column0', content: t('tour.acquisition.4.id') },
      { selector: '#column1', content: t('tour.acquisition.4.clouds') },
      { selector: '#column2', content: t('tour.acquisition.4.image') },
      { selector: '#columnaction', content: t('tour.acquisition.4.action') },
    );
  }

  return (
    <Box>
      <HelpButton
        onClickFunction={() => {
          setIsTourOpen(true);
        }}
      />
      {isSearching && <ActivityIndicator textual customSentence={t('forms.core.loading.searchingImages')} />}

      <Box className={classes.wrapper} display="flex" alignItems="center" flexDirection="column">
        <Box display="flex" gap={2} mb={2}>
          <DateInput
            label={t('forms.acquisition.3.startDate')}
            value={start}
            onBlur={(newStart) => onChange(newStart, end)}
            charLanguage={t('forms.transectEvolution.chartLanguage')}
            showCalendarPicker
          />
          <DateInput
            label={t('forms.acquisition.3.endDate')}
            value={end}
            onBlur={(newEnd) => onChange(start, newEnd)}
            charLanguage={t('forms.transectEvolution.chartLanguage')}
            showCalendarPicker
          />
        </Box>

        <CloudSelector level={cloudCoverage} onChange={(cloudCoverage) => setcloudCoverage(cloudCoverage)} />

        <Button
          className={classes.margin}
          variant="contained"
          color="primary"
          disabled={isSearching}
          onClick={handleSearch}>
          {t('forms.acquisition.3.searchImages')}
        </Button>

        {searched && !isSearching && !hasResults && (
          <Typography variant="subtitle1" align="center" className={classes.description}>
            {t('forms.acquisition.3.noImagesFound')}
          </Typography>
        )}

        {filteredOutByCloud && !isSearching && (
          <Typography variant="subtitle1" align="center" className={classes.description}>
            {t('forms.acquisition.3.noImagesMatchCloudFilter')}
          </Typography>
        )}

        {hasResults && !isSearching && !filteredOutByCloud && (
          <>
            <TimePeriodSelector
              key={visibleDates.map((entry) => entry.date).join('|')}
              dates={visibleDates.map((entry) => entry.date)}
              start={sliderStart}
              end={sliderEnd}
              onChange={(newStart, newEnd) => onChange(newStart, newEnd)}
            />

            <Box className={classes.description}>
              <Typography variant="subtitle1" align="center">
                {t('forms.acquisition.3.period')}: {formatDate(start)} {t('forms.acquisition.3.to')} {formatDate(end)}
              </Typography>
              <Typography variant="subtitle1" align="center">
                {formatDateDiff(start, end)},{' '}
                {datesBetween(visibleDates.map((entry) => entry.date), start, end).length}{' '}
                {t(
                  `forms.acquisition.3.imageQuantity.${
                    datesBetween(visibleDates.map((entry) => entry.date), start, end).length === 1
                      ? 'singular'
                      : 'plural'
                  }`,
                )}
              </Typography>
            </Box>

            {canReloadFullList && (
              <Box mb={2} display="flex" justifyContent="center">
                <Button variant="outlined" color="primary" onClick={handleReloadFullList}>
                  {t('forms.acquisition.4.reloadFullList')}
                </Button>
              </Box>
            )}

            <Box mb={1}>
              <ImportDatesButton images={narrowedDates} onImport={setSelected} />
            </Box>

            <Box width="100%">
              <ImageTable
                metadata={metadata}
                images={narrowedDates}
                selected={selected}
                onCheckboxChange={handleCheckboxChange}
                onSelectAll={handleSelectAll}
                variant="modern"
              />
            </Box>
          </>
        )}

        <StepperButtons
          navigate={navigate}
          nextText={t('forms.acquisition.4.next')}
          onNext={handleFinish}
          nextTarget={FINALIZE}
          nextDisabled={!hasResults || filteredOutByCloud}
        />
        <TourGuider steps={steps} isOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
      </Box>
    </Box>
  );
};

export default PeriodImageChooser;
